import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Typography, Space } from 'antd';
import {
    TeamOutlined, DollarOutlined, RocketOutlined, CheckCircleOutlined,
    UserAddOutlined, LoginOutlined, GiftOutlined, TrophyOutlined
} from '@ant-design/icons';
import './AffiliePage.css';

const { Title, Paragraph, Text } = Typography;

const AffiliePage = () => {
    const navigate = useNavigate();

    const benefits = [
        {
            icon: <DollarOutlined />,
            title: 'Commissions attractives',
            description: 'Gagnez des commissions sur chaque chauffeur que vous recrutez et qui rejoint la plateforme TIGOON.'
        },
        {
            icon: <TeamOutlined />,
            title: 'Développez votre réseau',
            description: 'Construisez votre équipe de chauffeurs partenaires et augmentez vos revenus passifs.'
        },
        {
            icon: <RocketOutlined />,
            title: 'Outils de suivi',
            description: 'Accédez à un tableau de bord complet pour suivre vos parrainages et vos commissions en temps réel.'
        },
        {
            icon: <GiftOutlined />,
            title: 'Bonus de performance',
            description: 'Débloquez des bonus supplémentaires en atteignant vos objectifs de recrutement.'
        }
    ];

    const steps = [
        {
            number: '1',
            title: 'Inscrivez-vous',
            description: 'Remplissez le formulaire de candidature en quelques minutes.'
        },
        {
            number: '2',
            title: 'Validation',
            description: 'Notre équipe examine votre candidature sous 24-48h.'
        },
        {
            number: '3',
            title: 'Recevez votre code',
            description: 'Une fois approuvé, vous recevez votre code de parrainage unique.'
        },
        {
            number: '4',
            title: 'Recrutez & Gagnez',
            description: 'Partagez votre code et commencez à gagner des commissions !'
        }
    ];

    return (
        <div className="affiliate-page">
            {/* Hero Section */}
            <section className="affiliate-hero">
                <div className="affiliate-hero-content">
                    <Title level={1}>Programme Affiliés TIGOON</Title>
                    <Paragraph className="affiliate-hero-subtitle">
                        Rejoignez notre réseau de commerciaux et gagnez de l'argent en recrutant
                        des chauffeurs pour la plateforme TIGOON Connect.
                    </Paragraph>
                    <Space size="large" className="affiliate-hero-buttons">
                        <Button
                            type="primary"
                            size="large"
                            icon={<UserAddOutlined />}
                            onClick={() => navigate('/affilies/inscription')}
                        >
                            Devenir Affilié
                        </Button>
                        <Button
                            size="large"
                            icon={<LoginOutlined />}
                            onClick={() => navigate('/affilies/connexion')}
                        >
                            Espace Affilié
                        </Button>
                    </Space>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="benefits-section">
                <div className="section-container">
                    <Title level={2} className="section-title">Pourquoi devenir affilié ?</Title>
                    <Row gutter={[24, 24]}>
                        {benefits.map((benefit, index) => (
                            <Col xs={24} sm={12} md={6} key={index}>
                                <Card className="benefit-card" hoverable>
                                    <div className="benefit-icon">{benefit.icon}</div>
                                    <Title level={4}>{benefit.title}</Title>
                                    <Paragraph>{benefit.description}</Paragraph>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* How it works Section */}
            <section className="how-it-works-section">
                <div className="section-container">
                    <Title level={2} className="section-title">Comment ça marche ?</Title>
                    <Row gutter={[24, 24]} justify="center">
                        {steps.map((step, index) => (
                            <Col xs={24} sm={12} md={6} key={index}>
                                <div className="step-card">
                                    <div className="step-number">{step.number}</div>
                                    <Title level={4}>{step.title}</Title>
                                    <Paragraph>{step.description}</Paragraph>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section">
                <div className="section-container">
                    <Row gutter={[48, 24]} justify="center">
                        <Col xs={12} md={6}>
                            <div className="stat-item">
                                <div className="stat-value">500+</div>
                                <Text className="stat-label">Affiliés actifs</Text>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="stat-item">
                                <div className="stat-value">2000+</div>
                                <Text className="stat-label">Chauffeurs recrutés</Text>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="stat-item">
                                <div className="stat-value">10M+</div>
                                <Text className="stat-label">FCFA distribués</Text>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="stat-item">
                                <div className="stat-value">24h</div>
                                <Text className="stat-label">Délai d'approbation</Text>
                            </div>
                        </Col>
                    </Row>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="section-container">
                    <Card className="cta-card">
                        <TrophyOutlined className="cta-icon" />
                        <Title level={2}>Prêt à rejoindre l'aventure ?</Title>
                        <Paragraph>
                            Inscrivez-vous maintenant et commencez à gagner des commissions
                            dès que vos premiers chauffeurs rejoignent TIGOON.
                        </Paragraph>
                        <Space size="large">
                            <Button
                                type="primary"
                                size="large"
                                icon={<UserAddOutlined />}
                                onClick={() => navigate('/affilies/inscription')}
                            >
                                S'inscrire maintenant
                            </Button>
                        </Space>
                    </Card>
                </div>
            </section>

            {/* FAQ Preview */}
            <section className="faq-section">
                <div className="section-container">
                    <Title level={2} className="section-title">Questions fréquentes</Title>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={12}>
                            <Card className="faq-card">
                                <Title level={5}>Combien puis-je gagner ?</Title>
                                <Paragraph>
                                    Vos gains dépendent du nombre de chauffeurs que vous recrutez.
                                    Chaque chauffeur actif vous rapporte une commission. Plus vous recrutez, plus vous gagnez !
                                </Paragraph>
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card className="faq-card">
                                <Title level={5}>Comment recevoir mes commissions ?</Title>
                                <Paragraph>
                                    Les commissions sont créditées sur votre compte et peuvent être retirées
                                    via Wave, Orange Money ou virement bancaire.
                                </Paragraph>
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card className="faq-card">
                                <Title level={5}>Y a-t-il des frais d'inscription ?</Title>
                                <Paragraph>
                                    Non, l'inscription au programme affiliés est 100% gratuite.
                                    Vous ne payez rien pour devenir affilié TIGOON.
                                </Paragraph>
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card className="faq-card">
                                <Title level={5}>Comment fonctionne le code de parrainage ?</Title>
                                <Paragraph>
                                    Chaque affilié reçoit un code unique (ex: TIGOON-ABC123).
                                    Les chauffeurs entrent ce code lors de leur inscription pour être liés à vous.
                                </Paragraph>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </section>
        </div>
    );
};

export default AffiliePage;
